import React from 'react';
import Tag from '@monofront/design-system/components/Tag';
import ProductImage from '@components/ProductImage';
import ButtonShowCount from '@components/ButtonShowCount';
import Link from 'next/link';
import ButtonAddToCart from '@components/ButtonAddToCart';
import { LinkToProduct } from '@components/LinkToProduct';
import { IProduct, IShoppingCartProduct } from '@monofront/types';
import {
  getUnitOfMeasurement,
  getFinance,
  getFormattedUnitPrice,
  getMembershipFormattedPrice,
  getPercentageChange,
  getMaxQuantity,
  format,
} from '@monofront/business/utils';
import { useFormatProductWeight } from '@monofront/business/hooks/product/useFormatProductWeight';

// TODO: mutualize products structure https://lafourche.atlassian.net/browse/LF-376
// then, no need this "hybrid" product interface
type IOOSModalCardProductProps = {
  editCountCallback?: (newCount: number) => Promise<void>;
} & (
  | {
      product: IProduct;
      isDisabled?: false;
    }
  | {
      product: IShoppingCartProduct;
      isDisabled?: true;
    }
);

const OOSModalCardProduct: React.FC<IOOSModalCardProductProps> = ({
  product,
  isDisabled,
  editCountCallback,
}) => {
  const unitOfMeasurement = getUnitOfMeasurement(product);
  const { formatProductWeight } = useFormatProductWeight();
  const formattedProductWeight = formatProductWeight(getFinance(product));

  const formattedUnitPrice = getFormattedUnitPrice(product);
  const formattedPrice = getMembershipFormattedPrice(product);
  const discountPrice = getPercentageChange(
    product.compare_at_price,
    product.price,
  );
  const formattedCompareAtPrice = format(product.compare_at_price);
  const maxQuantity = getMaxQuantity(product);

  return (
    <div className="oos-card-product-container">
      <div className="image">
        <LinkToProduct handle={product.handle}>
          <ProductImage
            img={product.image}
            quality={1}
            handle={product.handle}
            title={product.title}
          />
        </LinkToProduct>
      </div>
      <div className="all-details">
        <Link href={`/products/${product.handle}`} legacyBehavior>
          <a className="title">
            <span>{product.title}</span>
          </a>
        </Link>
        <div>
          <span className="vendor">{product.vendor}</span>
        </div>
        <p className="weight-and-price-by-unit">
          {formattedProductWeight && (
            <span className="weight">{formattedProductWeight}</span>
          )}
          {unitOfMeasurement && formattedUnitPrice ? (
            <span className="price-by-unit">
              {formattedUnitPrice}/{unitOfMeasurement}
            </span>
          ) : null}
        </p>
        <div className="price-and-compared-discount">
          <span className="price">
            {(product.price && format(product.price)) || formattedPrice}
          </span>
          <div className="compared-discount">
            {discountPrice && discountPrice > 0 ? (
              <div className="discount-percent">
                <Tag variant="secondary">{`-${discountPrice.toFixed(0)}%`}</Tag>
              </div>
            ) : null}
            {product.compare_at_price &&
            product.compare_at_price > 0 &&
            discountPrice ? (
              <span className="compared-price">{formattedCompareAtPrice}</span>
            ) : null}
          </div>
        </div>
      </div>
      <div className="btn-add">
        {isDisabled ? (
          <ButtonShowCount count={product.quantity} size="small" />
        ) : (
          <ButtonAddToCart
            shoppingCartProduct={product}
            max={maxQuantity}
            isDisabled={product.inventory_quantity <= 0}
            editCountCallback={editCountCallback}
            isReplacement
          />
        )}
      </div>
      <style jsx>{`
        .oos-card-product-container {
          height: 100%;
          max-height: 318px;
          min-width: 190px;
          display: flex;
          flex-direction: column;
          justify-content: flex-start;
          position: relative;
          max-width: 190px;
          pointer-events: ${isDisabled ? 'none' : 'inherit'};
          opacity: ${isDisabled ? '0.5' : '1'};
        }

        .image {
          width: 160px;
          height: 160px;
          align-self: center;
          margin-bottom: var(--space-2);
        }

        .all-details {
          display: flex;
          flex-direction: column;
          height: 100%;
        }

        .price-by-unit {
          font-size: var(--font-size-1);
        }

        .title {
          display: block;
          font-size: var(--font-size-2);
        }

        .weight {
          margin-right: var(--space-2);
        }

        .title,
        .vendor {
          cursor: pointer;
        }

        .vendor {
          color: var(--clr-grey-4);
        }

        .weight-and-price-by-unit {
          color: var(--clr-grey-4);
          display: flex;
          height: 100%;
        }

        .price {
          font-size: var(--font-size-3);
          font-family: var(--font-family-1);
        }

        .compared-discount {
          display: flex;
          align-items: center;
        }

        .compared-price {
          margin-left: var(--space-2);
          text-decoration: line-through;
          font-size: var(--font-size-1);
          color: var(--clr-grey-1);
        }

        .discount-percent > :global(div) {
          font-size: var(--font-size-1);
        }

        .btn-add {
          position: absolute;
          right: 0;
          bottom: 0;
        }

        @media (max-width: 800px) {
          .oos-card-product-container {
            pointer-events: ${isDisabled ? 'none' : 'inherit'};
            opacity: ${isDisabled ? '0.5' : '1'};
            flex-direction: row;
            width: 100%;
            max-width: inherit;
            justify-content: flex-start;
          }

          .all-details {
            height: inherit;
          }

          .image {
            width: 56px;
            height: 56px;
            min-width: 56px;
            min-height: 56px;
            align-self: flex-start;
            margin-right: var(--space-4);
          }

          .title {
            margin-bottom: var(--space-1);
          }

          .vendor {
            display: none;
          }

          .weight-and-price-by-unit {
            margin-bottom: var(--space-1);
            display: inherit;
            height: inherit;
          }

          .price-and-compared-discount {
            display: flex;
            flex-direction: row;
          }

          .price {
            margin-right: var(--space-2);
          }
        }
      `}</style>
    </div>
  );
};

export default OOSModalCardProduct;
